import { useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import _get from 'lodash/get';
import * as Yup from 'yup';
import {
  AccessComponentEnum,
  AccessLevelEnum,
  CompanyPlanUpgradeFeatureEnum,
  useEdmDocumentRenewalReminderTemplateQuery,
} from 'graphql-common';
import useOutletContext, { ContextProps } from 'hooks/useOutletContext';
import BreadCrumbs from '@lib/components/BreadCrumbs/BreadCrumbs';
import CircleLoader from '@lib/components/CircleLoader/CircleLoader';
import FeatureLockState from '@lib/components/FeatureLockState';
import PageBody from '@lib/layouts/DashboardLayout/PageBody/PageBody';
import PageHeader from '@lib/layouts/DashboardLayout/PageHeader/PageHeader';
import WhiteBox from '@lib/components/WhiteBox/WhiteBox';
import DashboardContentMaxWidth from '@lib/layouts/DashboardLayout/DashboardContentMaxWidth/DashboardContentMaxWidth';
import DashboardTitleHeader from '@lib/layouts/DashboardLayout/DashboardTitleHeader/DashboardTitleHeader';
import SupportiveMessage, {
  SupportiveMessageColor,
  SupportiveMessageType,
} from '@lib/components/SupportiveMessage/SupportiveMessage';
import Trans from '@lib/components/Trans/Trans';
import Form, { FormRef } from '@lib/components/ReactHookForm/FormContainer';
import MonthDayPicker from '@lib/components/DateTimePicker/MonthDayPicker';
import DocumentFormFieldNames from '@lib/enums/fieldNames/documentFormFieldNames';
import { PlanState } from '@lib/enums/plan';
import { FieldItem } from '@lib/interfaces/form';
import getRenewalReminderFields from 'routes/Documentation/DocumentForm/utils/getRenewalReminderFields';
import { getBreadCrumbsDataByKey } from 'utils/breadcrumbs';
import ReminderFormActions from './components/ReminderFormActions';
import getDefaultValues from './utils/getDefaultValues';
import useReminderActions from './utils/useReminderActions';
import styles from './Reminder.module.scss';

function withMonthDayDeadline(
  fields: FieldItem[],
  t: ReturnType<typeof useTranslation>['t'],
): FieldItem[] {
  return fields.map((field) => {
    if (!field.groupFields) return field;
    return {
      ...field,
      groupFields: field.groupFields.map((groupField) =>
        groupField.name === DocumentFormFieldNames.RenewalReminderDeadlineDate
          ? {
              ...groupField,
              element: MonthDayPicker,
              validation: Yup.string().when(
                DocumentFormFieldNames.RenewalReminderEnabled,
                (deps, schema) =>
                  _get(deps, 0)
                    ? schema.required(t('required-field-error'))
                    : schema.nullable(),
              ),
            }
          : groupField,
      ),
    };
  });
}

export default function Reminder() {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const formRef = useRef<FormRef>(undefined);

  const { getUserAccessByCompany, handleOpenPlanModal }: ContextProps =
    useOutletContext();
  // Company-plan access is the authoritative source for the locked-feature
  // state — user-role permissions can't override a plan lock. (Same pattern
  // as DocumentApprovals / DocumentAnalytics / DocumentCategories.) Non-Full-
  // Admin users are redirected to access-denied by ProtectedRoute before
  // reaching here, so the role-level check is unnecessary.
  const isPlanLocked =
    getUserAccessByCompany(AccessComponentEnum.DocumentationManagement) ===
    AccessLevelEnum.LimitedRead;

  const { data: queryData, loading: queryLoading } =
    useEdmDocumentRenewalReminderTemplateQuery({
      fetchPolicy: 'cache-and-network',
      skip: isPlanLocked,
    });

  const { loading: submitLoading, onSubmitForm } = useReminderActions();

  const defaultValues = useMemo(
    () => getDefaultValues(t, queryData?.data),
    [t, queryData?.data],
  );

  const fields = useMemo(
    () => withMonthDayDeadline(getRenewalReminderFields({ t }), t),
    [t],
  );

  const breadCrumbsData = useMemo(
    () => getBreadCrumbsDataByKey({ pathname, t }),
    [pathname, t],
  );

  const onCancel = useCallback(() => {
    formRef.current?.reset(defaultValues);
  }, [defaultValues]);

  const firstLoading = queryLoading && !queryData;

  if (isPlanLocked) {
    return (
      <>
        <BreadCrumbs data={breadCrumbsData} />
        <PageHeader title={t('reminder')} />
        <PageBody>
          <FeatureLockState
            title={t('unlock-renewal-reminder')}
            text={<Trans i18nKey="unlock-renewal-reminder-text" />}
            onUpgrade={() =>
              handleOpenPlanModal(PlanState.Locked, [
                CompanyPlanUpgradeFeatureEnum.Documentation,
              ])
            }
          />
        </PageBody>
      </>
    );
  }

  return (
    <>
      <BreadCrumbs data={breadCrumbsData} />
      {firstLoading ? (
        <CircleLoader />
      ) : (
        <PageBody withoutHeader>
          <DashboardContentMaxWidth>
            <WhiteBox>
              <DashboardTitleHeader
                title={t('document-renewal-reminder')}
                bottomSpace="middle"
              />
              <div className={styles.info}>
                <SupportiveMessage
                  color={SupportiveMessageColor.Gray}
                  type={SupportiveMessageType.Transparent}
                  leftIcon="info"
                  text={<Trans i18nKey="document-renewal-reminder-desc" />}
                />
              </div>
              <Form
                actionsComponent={ReminderFormActions}
                actionsComponentProps={{ onCancel }}
                defaultValues={defaultValues}
                fields={fields}
                formRef={formRef}
                loading={submitLoading}
                onSubmit={onSubmitForm}
              />
            </WhiteBox>
          </DashboardContentMaxWidth>
        </PageBody>
      )}
    </>
  );
}
